import { fetchDashboardDataFromSheet } from "../../api/sheetsApi";
import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { BadgePercent, Bus, Map, ShieldCheck, UserRound, Users } from "lucide-react";
import OverviewHeader from "./OverviewHeader";

const campusColumns = [
  { key: "perumbakkam", label: "Perumbakkam", color: "#0EA5E9" },
  { key: "kvr", label: "KVR", color: "#84CC16" },
  { key: "thiruvanmiyur", label: "Thiruvanmiyur", color: "#EAB308" },
  { key: "thirumudivakkam", label: "Thirumudivakkam", color: "#A855F7" },
  { key: "hyderbad", label: "Hyderabad", color: "#F97316" },
];

const statConfig = {
  "Total Bus Students": {
    title: "Bus Students",
    subtitle: "Group Total",
    color: "bg-pink-50",
    iconBg: "bg-pink-500",
    textColor: "text-pink-600",
    icon: <Users size={18} />,
  },
  "Total Buses": {
    title: "Total Buses",
    subtitle: "Fleet Size",
    color: "bg-sky-50",
    iconBg: "bg-blue-600",
    textColor: "text-sky-600",
    icon: <Bus size={18} />,
  },
  "Total Drivers": {
    title: "Drivers",
    subtitle: "All Campuses",
    color: "bg-red-50",
    iconBg: "bg-red-500",
    textColor: "text-red-600",
    icon: <UserRound size={18} />,
  },
  "Total Routes": {
    title: "Routes",
    subtitle: "Active Routes",
    color: "bg-yellow-50",
    iconBg: "bg-yellow-500",
    textColor: "text-yellow-600",
    icon: <Map size={18} />,
  },
  "Total Attenders": {
    title: "Attenders",
    subtitle: "Bus Monitors",
    color: "bg-lime-50",
    iconBg: "bg-lime-500",
    textColor: "text-lime-600",
    icon: <ShieldCheck size={18} />,
  },
  "Total Fee Collected": {
    title: "Fee Collected",
    subtitle: "All Terms",
    color: "bg-cyan-50",
    iconBg: "bg-cyan-500",
    textColor: "text-cyan-600",
    icon: <BadgePercent size={18} />,
  },
  "Term 1 Fee Collected": {
    title: "Term 1 Collected",
    subtitle: "Students Paid",
    color: "bg-emerald-50",
    iconBg: "bg-emerald-500",
    textColor: "text-emerald-600",
    icon: <BadgePercent size={18} />,
  },
};

const statOrder = [
  "Total Bus Students",
  "Total Buses",
  "Total Drivers",
  "Total Routes",
  "Total Attenders",
  "Total Fee Collected",
  "Term 1 Fee Collected",
];

const getMetricLabel = (row) =>
  row?.metric || row?.category || row?.name || row?.type || "";

const getCampusValue = (row, campusKey) => {
  const raw = campusKey === "hyderbad" ? (row?.hyderbad ?? row?.hyderabad) : row?.[campusKey];
  if (typeof raw === "string" && raw.includes("%")) {
    return raw;
  }

  return Number(raw || 0);
};

const getGroupTotal = (row) => {
  return row?.groupTotal ?? "0";
};

const formatValue = (value, suffix = "") => {
  if (typeof value === "string" && value.includes("%")) {
    return value;
  }

  const numberValue = Number(value || 0);

  return `${numberValue.toLocaleString("en-IN")}${suffix}`;
};

const getMetricSuffix = (metric = "") => (metric.includes("%") ? "%" : "");

const getMetricRow = (data, metric) =>
  data.find((row) => getMetricLabel(row).toLowerCase() === metric.toLowerCase());

const getStats = (data) =>
  statOrder.map((metric) => {
    const row = getMetricRow(data, metric);
    const config = statConfig[metric];
    const value = getGroupTotal(row);

    return {
      ...config,
      value: formatValue(value, config.suffix),
    };
  });

const getPieData = (data) => {
  const busStudents = getMetricRow(data, "Total Bus Students");

  return campusColumns
    .map((campus) => ({
      name: campus.label,
      value: getCampusValue(busStudents, campus.key),
      color: campus.color,
    }))
    .filter((item) => item.value > 0);
};

const getTrendTerm = (metric = "") => {
  const feeMatch = metric.match(/^Fee\s*\((.+)\)$/i);
  if (feeMatch) {
    return feeMatch[1];
  }

  const termMatch = metric.match(/^(Term\s+[IVXLC]+|Term\s+\d+)/i);
  return termMatch?.[1] || "";
};

// getCampusValue deliberately keeps "62%"-style values as strings so the breakdown table can
// display them verbatim, but recharts needs a plain number to plot a line — a string Y-value
// silently draws nothing (no error, just a blank chart). This term-fee sheet stores its % rows
// as text (e.g. "Term 1 Fee Collected" → "100%"), so the chart line was always empty even though
// the table right below it showed real percentages.
const getCampusNumericValue = (row, campusKey) => {
  const raw = getCampusValue(row, campusKey);
  if (typeof raw !== "string") return raw;
  const parsed = Number(raw.replace(/[%,]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTrendData = (data) =>
  data
    .map((row) => {
      const metric = getMetricLabel(row);
      const term = getTrendTerm(metric);
      const isFeeCollectedRow =
        term && metric.toLowerCase().includes("fee") && !metric.includes("%");

      if (!isFeeCollectedRow) {
        return null;
      }

      return {
        term,
        ...campusColumns.reduce(
          (values, campus) => ({
            ...values,
            [campus.key]: getCampusNumericValue(row, campus.key),
          }),
          {}
        ),
      };
    })
    .filter(Boolean);

export default function TransportDashboard() {
  const [transportData, setTransportData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchTransportData = async () => {
      try {
        const data = await fetchDashboardDataFromSheet();
        setTransportData(data?.transportRows || []);
        setCurrentMonth(data?.currentMonth || "May 2026");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to load transport data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTransportData();
  }, []);

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-gray-100 p-2">
        <div className="rounded-xl bg-white px-5 py-4 text-sm font-medium text-red-500 shadow-sm">
          <p>{error}</p>
          
        </div>
      </div>
    );
  }

  const stats = getStats(transportData);
  const pieData = getPieData(transportData);
  const pieTotal = pieData.reduce((sum, item) => sum + item.value, 0);
  const trendData = getTrendData(transportData);
  const tableData = transportData;

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      <OverviewHeader currentMonth={currentMonth} />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <div
            key={item.title}
            className={`${item.color} rounded-2xl p-4 shadow-sm`}
          >
            <div
              className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg text-white ${item.iconBg}`}
            >
              {item.icon}
            </div>

            <h3 className="text-sm font-medium text-slate-600">
              {item.title}
            </h3>

            <p className="mt-1 text-2xl font-bold text-slate-800">
              {item.value}
            </p>

            <p className={`mt-1 text-xs font-medium ${item.textColor}`}>
              {item.subtitle}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h3 className="mb-5 text-lg font-semibold text-slate-700">
            Bus Students by Campus
          </h3>

          <div className="flex flex-col items-center justify-between gap-5 lg:flex-row">
            <div className="relative h-[240px] w-full max-w-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                    itemStyle={{ color: "#334155", fontWeight: 500 }}
                    formatter={(value, name) => [
                      `${formatValue(value)} (${pieTotal ? ((value / pieTotal) * 100).toFixed(1) : "0"}%)`,
                      name,
                    ]}
                  />
                  <Pie
                    data={pieData}
                    dataKey="value"
                    innerRadius={65}
                    outerRadius={85}
                    paddingAngle={4}
                    minAngle={3}
                    stroke="none"
                    isAnimationActive={false}
                  >
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-bold text-slate-800">{formatValue(pieTotal)}</p>
                <p className="text-xs font-medium text-slate-400">Bus Students</p>
              </div>
            </div>

            <div className="space-y-4">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center gap-3">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ background: item.color }}
                  />

                  <p className="text-sm text-slate-600">
                    {item.name} ({formatValue(item.value)} · {pieTotal ? ((item.value / pieTotal) * 100).toFixed(1) : "0"}%)
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <h3 className="mb-5 text-lg font-semibold text-slate-700">
            Fee Collection Trend (Students Paid)
          </h3>

          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis 
                  dataKey="term" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: "#64748b", fontSize: 12 }} 
                  dy={10} 
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  tickFormatter={(value) => value.toLocaleString("en-IN")}
                  dx={-10}
                />
                <Tooltip
                  contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                  labelStyle={{ fontWeight: "bold", color: "#1e293b", marginBottom: "4px" }}
                  formatter={(value, name) => [value.toLocaleString("en-IN"), name]}
                />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px", fontSize: "13px" }} />

                {/* Permanent value labels used to be drawn on only 2 of these 5 lines, and with
                    values this close together at Term 1/2 they overlapped into unreadable text.
                    The Tooltip above already surfaces every campus's exact value on hover, so the
                    lines speak for themselves here and only the active point highlights on hover. */}
                {campusColumns.map((campus) => (
                  <Line
                    key={campus.key}
                    type="monotone"
                    dataKey={campus.key}
                    name={campus.label}
                    stroke={campus.color}
                    strokeWidth={2.5}
                    dot={{ r: 3.5, strokeWidth: 2, fill: "#fff" }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="border-b px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-700">
            Campus-Wise Transport Breakdown
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-2 py-1">Metric</th>
                {campusColumns.map((campus) => (
                  <th key={campus.key} className="px-2 py-1">
                    {campus.label}
                  </th>
                ))}
                <th className="px-2 py-1">Group Total</th>
              </tr>
            </thead>

            <tbody>
              {tableData.map((row, index) => (
                <tr
                  key={getMetricLabel(row)}
                  className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}
                >
                  <td className="px-2 py-1 font-medium text-slate-700">
                    {getMetricLabel(row)}
                  </td>

                  {campusColumns.map((campus) => (
                    <td key={campus.key} className="px-2 py-1">
                      {formatValue(
                        getCampusValue(row, campus.key),
                        getMetricSuffix(getMetricLabel(row))
                      )}
                    </td>
                  ))}

                  <td className="px-2 py-1 font-semibold">
                    {formatValue(
                      getGroupTotal(row),
                      getMetricSuffix(getMetricLabel(row))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
